/**
 * Outil « Sommes partielles » — calculs (pur).
 *
 * Quatre familles de séries numériques de prépa, dont la nature est CONNUE
 * (le verdict vient du cours, jamais du calcul) :
 * - Riemann Σ 1/n^α (n ≥ 1) : converge ssi α > 1 ;
 * - alternée Σ (−1)ⁿ⁺¹/n^α (n ≥ 1) : converge pour α > 0 (critère spécial) ;
 * - géométrique Σ qⁿ (n ≥ 0) : converge ssi |q| < 1, somme 1/(1 − q) ;
 * - exponentielle Σ xⁿ/n! (n ≥ 0) : converge pour tout x, somme eˣ.
 *
 * Les sommes ζ(α) et η(α) se calculent par l'algorithme de Borwein (série
 * alternée accélérée), précis à 10⁻¹⁵ près pour α ∈ ]0 ; 3].
 */

import { rational } from '../../ui/exact';
import { formatNumber, formatSci } from '../../ui/format';

export type SeriesKind = 'riemann' | 'alternating' | 'geometric' | 'exponential';

export interface SeriesParams {
  /** exposant de Riemann */
  alpha: number;
  /** exposant de la série alternée */
  altAlpha: number;
  q: number;
  x: number;
}

export const RANGES = {
  alpha: { min: 0.5, max: 3, step: 0.1 },
  altAlpha: { min: 0.2, max: 2, step: 0.1 },
  q: { min: -1.2, max: 1.2, step: 0.05 },
  x: { min: -3, max: 3, step: 0.5 },
} as const;

export const DEFAULT_PARAMS: SeriesParams = { alpha: 2, altAlpha: 1, q: 0.5, x: 1 };

/** Nombres de termes proposés : les séries lentes vont loin, les rapides non. */
export const N_CHOICES: Record<SeriesKind, readonly number[]> = {
  riemann: [10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
  alternating: [10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
  geometric: [5, 10, 15, 20, 30, 50],
  exponential: [5, 10, 15, 20, 30],
};

export const EULER_GAMMA = 0.5772156649015329;

/** premier indice de la somme */
export function firstIndex(kind: SeriesKind): 0 | 1 {
  return kind === 'riemann' || kind === 'alternating' ? 1 : 0;
}

const near = (a: number, b: number) => Math.abs(a - b) < 1e-9;

/** uₙ */
export function term(kind: SeriesKind, p: SeriesParams, n: number): number {
  switch (kind) {
    case 'riemann':
      return 1 / n ** p.alpha;
    case 'alternating':
      return (n % 2 === 1 ? 1 : -1) / n ** p.altAlpha;
    case 'geometric':
      return p.q ** n;
    case 'exponential': {
      let t = 1;
      for (let k = 1; k <= n; k++) t *= p.x / k;
      return t;
    }
  }
}

/**
 * Sommes partielles S_first … S_N (tableau indexé par n − first). La somme
 * est compensée (Kahan) : 5 000 termes de 1/n² sans perte visible.
 */
export function partialSums(kind: SeriesKind, p: SeriesParams, n: number): number[] {
  const first = firstIndex(kind);
  const out: number[] = [];
  let s = 0;
  let c = 0;
  let t = 1; // terme courant de l'exponentielle
  for (let k = first; k <= n; k++) {
    let u: number;
    if (kind === 'exponential') {
      if (k > 0) t *= p.x / k;
      u = t;
    } else u = term(kind, p, k);
    const y = u - c;
    const z = s + y;
    c = z - s - y;
    s = z;
    out.push(s);
  }
  return out;
}

// ---------------------------------------------------------------------------
// ζ et η (Borwein, « An efficient algorithm for the Riemann zeta function »)

const BORWEIN_N = 30;
const BORWEIN_D: number[] = (() => {
  const n = BORWEIN_N;
  const fact = [1];
  for (let i = 1; i <= 2 * n; i++) fact.push(fact[i - 1]! * i);
  const d: number[] = [];
  let acc = 0;
  for (let i = 0; i <= n; i++) {
    acc += (fact[n + i - 1]! * 4 ** i) / (fact[n - i]! * fact[2 * i]!);
    d.push(n * acc);
  }
  return d;
})();

/** η(s) = Σ (−1)ⁿ⁺¹/nˢ pour s > 0 */
export function eta(s: number): number {
  const d = BORWEIN_D;
  const dn = d[BORWEIN_N]!;
  let sum = 0;
  for (let k = 0; k < BORWEIN_N; k++) {
    sum += ((k % 2 === 0 ? 1 : -1) * (d[k]! - dn)) / (k + 1) ** s;
  }
  return -sum / dn;
}

/** ζ(s) pour s > 0, s ≠ 1 (prolongement pour s < 1 : sert au terme constant). */
export function zeta(s: number): number {
  return eta(s) / (1 - 2 ** (1 - s));
}

// ---------------------------------------------------------------------------
// Verdict, somme, reste

export interface SumValue {
  value: number;
  tex: string;
  plain: string;
  /** bouts pour la feuille (forme exacte), absent si seulement approchée */
  atoms?: string[];
  /** pas de forme close connue */
  approxOnly?: boolean;
}

export interface Verdict {
  converges: boolean;
  /** −1 : pas de limite (oscille) ; 1 : tend vers +∞ ; 0 : converge */
  behaviour: -1 | 0 | 1;
  /** raison du cours, en une phrase */
  reason: string;
  sum: SumValue | null;
  /** équivalent de Sₙ quand elle tend vers +∞ (KaTeX) */
  growthTex?: string;
  growth?: (n: number) => number;
}

/** « 0,5 » → « 0{,}5 » pour KaTeX */
const tex = (v: number, digits = 2) =>
  formatNumber(v, digits).replace(',', '{,}').replace('−', '-');

export function verdict(kind: SeriesKind, p: SeriesParams): Verdict {
  switch (kind) {
    case 'riemann': {
      const a = p.alpha;
      if (a > 1 + 1e-9) {
        const v = zeta(a);
        const sum: SumValue = near(a, 2)
          ? {
              value: (Math.PI * Math.PI) / 6,
              tex: '\\frac{\\pi^2}{6}',
              plain: 'π²/6',
              atoms: ['π', '²', '÷', '6'],
            }
          : near(a, 3)
            ? { value: v, tex: '\\zeta(3)', plain: 'ζ(3)', approxOnly: true }
            : {
                value: v,
                tex: `\\zeta(${tex(a, 1)})`,
                plain: `ζ(${formatNumber(a, 1)})`,
                approxOnly: true,
              };
        return {
          converges: true,
          behaviour: 0,
          reason: 'Série de Riemann : elle converge si et seulement si α > 1.',
          sum,
        };
      }
      if (near(a, 1)) {
        return {
          converges: false,
          behaviour: 1,
          reason:
            'Série harmonique : elle diverge, mais très lentement — Sₙ = ln n + γ + o(1) (γ ≈ 0,577).',
          sum: null,
          growthTex: 'S_n = \\ln n + \\gamma + o(1)',
          growth: (n) => Math.log(n) + EULER_GAMMA,
        };
      }
      const c = zeta(a);
      const lead = near(a, 0.5) ? '2\\sqrt{n}' : `\\frac{n^{${tex(1 - a, 1)}}}{${tex(1 - a, 1)}}`;
      return {
        converges: false,
        behaviour: 1,
        reason: `Série de Riemann avec α ≤ 1 : elle diverge (1/n^α ≥ 1/n, et la série harmonique diverge).`,
        sum: null,
        growthTex: `S_n = ${lead} + \\zeta(${tex(a, 1)}) + o(1)`,
        growth: (n) => n ** (1 - a) / (1 - a) + c,
      };
    }
    case 'alternating': {
      const a = p.altAlpha;
      const v = eta(a);
      const sum: SumValue = near(a, 1)
        ? { value: Math.LN2, tex: '\\ln 2', plain: 'ln 2', atoms: ['ln', '(', '2', ')'] }
        : near(a, 2)
          ? {
              value: (Math.PI * Math.PI) / 12,
              tex: '\\frac{\\pi^2}{12}',
              plain: 'π²/12',
              atoms: ['π', '²', '÷', '12'],
            }
          : {
              value: v,
              tex: `\\eta(${tex(a, 1)})`,
              plain: `η(${formatNumber(a, 1)})`,
              approxOnly: true,
            };
      return {
        converges: true,
        behaviour: 0,
        reason:
          a <= 1 + 1e-9
            ? 'Critère spécial des séries alternées : 1/n^α décroît vers 0, donc la série converge — sans converger absolument (Σ 1/n^α diverge).'
            : 'Critère spécial des séries alternées : 1/n^α décroît vers 0, donc la série converge (et même absolument, car α > 1).',
        sum,
      };
    }
    case 'geometric': {
      const q = p.q;
      if (Math.abs(q) < 1 - 1e-9) {
        const qq = Math.round(q * 100);
        const ex = rational(100, 100 - qq);
        return {
          converges: true,
          behaviour: 0,
          reason:
            'Série géométrique : elle converge si et seulement si |q| < 1, de somme 1/(1 − q).',
          sum: { value: ex.value, tex: ex.tex, plain: ex.plain, atoms: ex.atoms },
        };
      }
      if (q >= 1 - 1e-9) {
        return {
          converges: false,
          behaviour: 1,
          reason: near(q, 1)
            ? 'q = 1 : Sₙ = n + 1 tend vers +∞ (le terme général ne tend pas vers 0).'
            : 'q > 1 : le terme général qⁿ tend vers +∞, la série diverge grossièrement.',
          sum: null,
        };
      }
      return {
        converges: false,
        behaviour: -1,
        reason: near(q, -1)
          ? 'q = −1 : Sₙ vaut 1, 0, 1, 0… — pas de limite (le terme général ne tend pas vers 0).'
          : 'q < −1 : les termes grandissent en changeant de signe, Sₙ oscille de plus en plus fort — pas de limite.',
        sum: null,
      };
    }
    case 'exponential': {
      const x = p.x;
      const xEx = rational(Math.round(x * 2), 2);
      return {
        converges: true,
        behaviour: 0,
        reason: near(x, 0)
          ? 'x = 0 : u₀ = 1 et uₙ = 0 pour n ≥ 1, donc Sₙ = 1 pour tout n (et e⁰ = 1).'
          : 'Série exponentielle : elle converge pour tout réel x (d’Alembert : |uₙ₊₁/uₙ| = |x|/(n + 1) → 0), de somme eˣ.',
        sum: near(x, 0)
          ? { value: 1, tex: '1', plain: '1', atoms: ['1'] }
          : {
              value: Math.exp(x),
              tex: near(x, 1) ? '\\mathrm{e}' : `\\mathrm{e}^{${xEx.tex}}`,
              plain: near(x, 1)
                ? 'e'
                : Number.isInteger(x) && x > 0
                  ? `e^${xEx.plain}`
                  : `e^(${xEx.plain})`,
              atoms: ['exp', '(', ...xEx.atoms, ')'],
            },
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Restes précis (jamais S − Sₙ quand Sₙ est trop proche de S : bruit d'arrondi)

/** sommes directes jusque-là, Euler–Maclaurin (ou Boole) au-delà */
const DIRECT = 40;

function directSum(kind: 'riemann' | 'alternating', a: number, n: number): number {
  let s = 0;
  for (let k = 1; k <= n; k++) s += (kind === 'alternating' && k % 2 === 0 ? -1 : 1) / k ** a;
  return s;
}

/** Sₙ de Σ 1/k^α (k = 1 … n), pour α ≤ 1 (la série diverge) */
export function riemannPartial(a: number, n: number): number {
  if (n <= DIRECT) return directSum('riemann', a, n);
  if (near(a, 1))
    return Math.log(n) + EULER_GAMMA + 1 / (2 * n) - 1 / (12 * n * n) + 1 / (120 * n ** 4);
  return (
    zeta(a) +
    n ** (1 - a) / (1 - a) +
    n ** -a / 2 -
    (a * n ** (-a - 1)) / 12 +
    (a * (a + 1) * (a + 2) * n ** (-a - 3)) / 720
  );
}

/** Rₙ = Σ_{k>n} 1/k^α (α > 1), précis en relatif (Euler–Maclaurin) */
function riemannTail(a: number, n: number): number {
  if (n <= DIRECT) return zeta(a) - directSum('riemann', a, n);
  return (
    n ** (1 - a) / (a - 1) -
    n ** -a / 2 +
    (a * n ** (-a - 1)) / 12 -
    (a * (a + 1) * (a + 2) * n ** (-a - 3)) / 720
  );
}

/** |Rₙ| = |Σ_{k>n} (−1)ᵏ⁺¹/k^α|, précis en relatif (formule d'Euler–Boole) */
function alternatingTail(a: number, n: number): number {
  if (n <= DIRECT) return Math.abs(eta(a) - directSum('alternating', a, n));
  const m = n + 1;
  return (
    0.5 * m ** -a +
    (a / 4) * m ** (-a - 1) -
    ((a * (a + 1) * (a + 2)) / 48) * m ** (-a - 3) +
    ((a * (a + 1) * (a + 2) * (a + 3) * (a + 4)) / 480) * m ** (-a - 5)
  );
}

/** |Rₙ| = |Σ_{k>n} xᵏ/k!|, sommé directement (les termes décroissent vite) */
function exponentialTail(x: number, n: number): number {
  let t = term('exponential', { ...DEFAULT_PARAMS, x }, n + 1);
  let s = 0;
  for (let k = n + 1; k <= n + 400 && t !== 0; k++) {
    s += t;
    t *= x / (k + 1);
  }
  return Math.abs(s);
}

/** |S − Sₙ| calculé sans soustraire deux nombres presque égaux ; null si divergente */
export function remainder(kind: SeriesKind, p: SeriesParams, n: number): number | null {
  if (!verdict(kind, p).converges) return null;
  switch (kind) {
    case 'riemann':
      return riemannTail(p.alpha, n);
    case 'alternating':
      return alternatingTail(p.altAlpha, n);
    case 'geometric':
      return Math.abs(p.q) ** (n + 1) / (1 - p.q);
    case 'exponential':
      return exponentialTail(p.x, n);
  }
}

/**
 * Ce que le cours dit du reste : un majorant (ou le reste exact pour la
 * géométrique). null si la série diverge ou si le majorant ne s'applique pas.
 */
export function remainderBound(
  kind: SeriesKind,
  p: SeriesParams,
  n: number,
): { value: number; text: string; exact?: boolean } | null {
  switch (kind) {
    case 'riemann':
      if (p.alpha <= 1 + 1e-9) return null;
      return {
        value: n ** (1 - p.alpha) / (p.alpha - 1),
        text: 'comparaison série-intégrale : Rₙ ≤ n^(1−α)/(α − 1)',
      };
    case 'alternating':
      return {
        value: 1 / (n + 1) ** p.altAlpha,
        text: 'critère spécial : |Rₙ| ≤ |uₙ₊₁| et S est entre deux sommes consécutives',
      };
    case 'geometric':
      if (Math.abs(p.q) >= 1 - 1e-9) return null;
      return {
        value: Math.abs(p.q) ** (n + 1) / (1 - p.q),
        text: 'reste exact : Rₙ = qⁿ⁺¹/(1 − q)',
        exact: true,
      };
    case 'exponential': {
      // |uₙ₊ₖ₊₁| ≤ |uₙ₊₁|·(|x|/(n + 2))ᵏ : reste dominé par une géométrique
      const r = Math.abs(p.x) / (n + 2);
      if (r >= 1) return null;
      return {
        value: Math.abs(term('exponential', p, n + 1)) / (1 - r),
        text: 'termes dominés par une géométrique : |Rₙ| ≤ |uₙ₊₁|/(1 − |x|/(n + 2))',
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Seuils (formules précises : aucune boucle de millions de termes)

/** au-delà, le rang est donné en ordre de grandeur (2 chiffres) */
export const EXACT_LIMIT = 1e7;

export interface Threshold {
  n: number;
  /** rang au chiffre près (sinon ordre de grandeur, n > EXACT_LIMIT) */
  exact: boolean;
  /** rang que le majorant du cours suffit à garantir, s'il diffère */
  guaranteed?: number;
}

/** Plus petit entier n ≥ from tel que ok(n), pour ok croissant (faux puis vrai). */
function firstTrue(ok: (n: number) => boolean, from: number): number {
  if (ok(from)) return from;
  let lo = from;
  let hi = Math.max(from + 1, 2 * from);
  while (!ok(hi)) {
    lo = hi;
    hi *= 2;
    if (hi > 1e300) return Infinity;
  }
  // ok(lo) faux, ok(hi) vrai
  while (hi - lo > 1 && hi - lo > hi * 1e-13) {
    const mid = Math.floor((lo + hi) / 2);
    if (ok(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

const threshold = (n: number, guaranteed?: number): Threshold => ({
  n,
  exact: n <= EXACT_LIMIT,
  ...(guaranteed !== undefined && guaranteed !== n ? { guaranteed } : {}),
});

/**
 * Premier n à partir duquel |S − Sₖ| < ε pour tout k ≥ n (série convergente).
 * Riemann, alternée, géométrique : |S − Sₖ| décroît, le premier rang suffit.
 * Exponentielle : le reste peut grossir d'abord (x < 0) : dernier rang hors
 * de ε, plus un. `guaranteed` : rang qu'on justifie avec le majorant du cours.
 */
export function precisionRank(kind: SeriesKind, p: SeriesParams, eps: number): Threshold | null {
  if (!verdict(kind, p).converges) return null;
  const rem = (n: number) => remainder(kind, p, n)!;
  const bound = (n: number) => remainderBound(kind, p, n)?.value ?? Infinity;
  switch (kind) {
    case 'riemann':
    case 'alternating':
      return threshold(
        firstTrue((n) => rem(n) < eps, 1),
        firstTrue((n) => bound(n) < eps, 1),
      );
    case 'geometric':
      return threshold(firstTrue((n) => rem(n) < eps, 0));
    case 'exponential': {
      let last = -1;
      for (let n = 0; n <= 200; n++) if (!(rem(n) < eps)) last = n;
      let g = Math.max(0, Math.ceil(Math.abs(p.x)) - 1);
      while (!(bound(g) < eps) && g < 400) g++;
      return threshold(last + 1, Math.max(g, last + 1));
    }
  }
}

/** Premier n tel que Sₙ > A (série qui tend vers +∞). */
export function divergenceRank(kind: SeriesKind, p: SeriesParams, a: number): Threshold | null {
  const v = verdict(kind, p);
  if (v.behaviour !== 1) return null;
  if (kind === 'riemann') return threshold(firstTrue((n) => riemannPartial(p.alpha, n) > a, 1));
  // géométrique, q ≥ 1 : Sₙ dépasse A vite
  let sum = 0;
  for (let k = 0; ; k++) {
    sum += p.q ** k;
    if (sum > a) return threshold(k);
  }
}

// ---------------------------------------------------------------------------
// Écritures

/** 12 367 · 2,7 × 10⁸ (au-delà de 10⁷, ou toujours si `approx`) */
export function formatCount(n: number, approx = false): string {
  if (!Number.isFinite(n)) return '∞';
  if (n < EXACT_LIMIT && !approx)
    return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
  return formatSci(n, 1);
}

/** Terme général en KaTeX : \frac{1}{n^{2}}, \left(-0{,}5\right)^n… */
export function termTex(kind: SeriesKind, p: SeriesParams): string {
  const pow = (a: number) => (near(a, 1) ? 'n' : near(a, 0.5) ? '\\sqrt{n}' : `n^{${tex(a, 1)}}`);
  switch (kind) {
    case 'riemann':
      return `\\frac{1}{${pow(p.alpha)}}`;
    case 'alternating':
      return `\\frac{(-1)^{n+1}}{${pow(p.altAlpha)}}`;
    case 'geometric':
      return near(p.q, 1)
        ? '1^n'
        : p.q < 0 || !Number.isInteger(p.q)
          ? `\\left(${tex(p.q)}\\right)^{n}`
          : `${tex(p.q)}^{n}`;
    case 'exponential':
      return near(p.x, 1)
        ? '\\frac{1}{n!}'
        : p.x < 0 || !Number.isInteger(p.x)
          ? `\\frac{\\left(${tex(p.x, 1)}\\right)^{n}}{n!}`
          : `\\frac{${tex(p.x, 1)}^{n}}{n!}`;
  }
}

/** Σ en KaTeX, avec le premier indice */
export function seriesTex(kind: SeriesKind, p: SeriesParams): string {
  return `\\sum_{n \\geq ${firstIndex(kind)}} ${termTex(kind, p)}`;
}

/** Programme Python qui calcule S_N */
export function pythonProgram(kind: SeriesKind, p: SeriesParams): string {
  const num = (v: number) => formatNumber(v, 2).replace(',', '.').replace('−', '-');
  if (kind === 'exponential') {
    // terme par récurrence : uₙ = uₙ₋₁ × x / n (jamais xⁿ et n! séparément)
    return `def S(N, x=${num(p.x)}):
    u = 1
    s = u
    for n in range(1, N + 1):
        u = u * x / n
        s = s + u
    return s`;
  }
  const body: Record<Exclude<SeriesKind, 'exponential'>, string> = {
    riemann: `1 / n**${num(p.alpha)}`,
    alternating: `(-1)**(n + 1) / n**${num(p.altAlpha)}`,
    geometric: `(${num(p.q)})**n`,
  };
  return `def S(N):
    s = 0
    for n in range(${firstIndex(kind)}, N + 1):
        s = s + ${body[kind]}
    return s`;
}

// ---------------------------------------------------------------------------
// Défi « converge ou diverge ? »

export interface QuizItem {
  tex: string;
  converges: boolean;
  why: string;
}

export const QUIZ_BANK: readonly QuizItem[] = [
  { tex: '\\sum_{n \\geq 1} \\frac{1}{n}', converges: false, why: 'Série harmonique : Sₙ ≈ ln n.' },
  {
    tex: '\\sum_{n \\geq 1} \\frac{1}{n^2}',
    converges: true,
    why: 'Riemann avec α = 2 > 1 (somme π²/6).',
  },
  {
    tex: '\\sum_{n \\geq 1} \\frac{1}{\\sqrt{n}}',
    converges: false,
    why: 'Riemann avec α = 1/2 ≤ 1.',
  },
  {
    tex: '\\sum_{n \\geq 1} \\frac{1}{n^{1{,}01}}',
    converges: true,
    why: 'Riemann avec α = 1,01 > 1 : converge, mais avec une lenteur extrême.',
  },
  {
    tex: '\\sum_{n \\geq 1} \\frac{(-1)^n}{n}',
    converges: true,
    why: 'Critère spécial des séries alternées (somme −ln 2), sans convergence absolue.',
  },
  {
    tex: '\\sum_{n \\geq 0} (-1)^n',
    converges: false,
    why: 'Le terme général ne tend pas vers 0 : divergence grossière.',
  },
  {
    tex: '\\sum_{n \\geq 0} \\frac{1}{2^n}',
    converges: true,
    why: 'Géométrique de raison 1/2 : |q| < 1.',
  },
  { tex: '\\sum_{n \\geq 0} 1{,}1^n', converges: false, why: 'Géométrique de raison 1,1 ≥ 1.' },
  {
    tex: '\\sum_{n \\geq 0} \\frac{1}{n!}',
    converges: true,
    why: 'Série exponentielle : somme e.',
  },
  {
    tex: '\\sum_{n \\geq 0} \\frac{n}{n^2 + 1}',
    converges: false,
    why: 'Terme positif équivalent à 1/n : même nature que la série harmonique.',
  },
  {
    tex: '\\sum_{n \\geq 1} \\frac{1}{n(n+1)}',
    converges: true,
    why: '1/(n(n+1)) = 1/n − 1/(n+1) : somme télescopique, égale à 1.',
  },
  {
    tex: '\\sum_{n \\geq 1} \\ln\\left(1 + \\frac{1}{n}\\right)',
    converges: false,
    why: 'Télescopique : Sₙ = ln(n + 1) → +∞ (et le terme est équivalent à 1/n).',
  },
  {
    tex: '\\sum_{n \\geq 1} \\frac{\\sin n}{n^2}',
    converges: true,
    why: '|sin n / n²| ≤ 1/n² : convergence absolue.',
  },
  {
    tex: '\\sum_{n \\geq 1} \\left(1 - \\frac{1}{n}\\right)^n',
    converges: false,
    why: 'Le terme général tend vers 1/e ≠ 0 : divergence grossière.',
  },
  {
    tex: '\\sum_{n \\geq 0} \\frac{n^2}{2^n}',
    converges: true,
    why: 'D’Alembert : uₙ₊₁/uₙ → 1/2 < 1.',
  },
  {
    tex: '\\sum_{n \\geq 2} \\frac{1}{n \\ln n}',
    converges: false,
    why: 'Comparaison série-intégrale : ∫ dx/(x ln x) = ln(ln x) → +∞.',
  },
];

export const QUIZ_ROUNDS = 5;

/** Tirage de `QUIZ_ROUNDS` questions distinctes */
export function pickQuiz(rng: () => number): QuizItem[] {
  const pool = [...QUIZ_BANK];
  const out: QuizItem[] = [];
  while (out.length < QUIZ_ROUNDS && pool.length > 0) {
    const i = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
    out.push(pool.splice(i, 1)[0]!);
  }
  return out;
}

/** Fenêtre verticale du tracé : sommes affichées (finies, ≤ 10⁶) et la somme S. */
export function yRange(values: readonly number[], sum: number | null): [number, number] {
  const finite = values.filter((v) => Number.isFinite(v) && Math.abs(v) <= 1e6);
  if (sum !== null) finite.push(sum);
  if (finite.length === 0) return [-1, 1];
  let lo = Math.min(...finite, 0);
  let hi = Math.max(...finite, 0);
  if (hi - lo < 0.5) {
    const c = (lo + hi) / 2;
    lo = c - 0.25;
    hi = c + 0.25;
  }
  const pad = (hi - lo) * 0.08;
  return [lo - pad, hi + pad];
}
